import { Megaphone, MegaphoneOff, Send } from 'lucide-react';
import { useCallback, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ConfirmDialog } from '../../../../components/ConfirmDialog';
import { IndexPage } from '../../../../components/index-page/IndexPage';
import type { ColumnDef, IndexQueryState } from '../../../../components/index-page/types';
import type { MenuItem } from '../../../../components/Menu';
import { useListSurface } from '../../../../components/table/listSurface';
import { StatusText } from '../../../../components/table/StatusText';
import { TextCell } from '../../../../components/table/TextCell';
import { useToast } from '../../../../components/toastContext';
import { ApiError } from '../../../../lib/apiError';
import { formatMoney } from '../../../../lib/formatMoney';
import { TagForJobPostingModal } from '../components/TagForJobPostingModal';
import { TagSelectedModal } from '../components/TagSelectedModal';
import { vacanciesApi } from '../vacanciesApi';
import { VACANCY_AUDIT_ENTITY_TYPES, type VacancyListItem } from '../vacancyTypes';

/**
 * Which confirmation is open — one piece of state, so two dialogs can never stack. Untag and Publish
 * WRITE, so they confirm; tagging COLLECTS the posting details, so it is a form.
 */
type Pending = { kind: 'untag'; vacancy: VacancyListItem } | { kind: 'publish'; vacancies: VacancyListItem[] };

/**
 * Every vacant plantilla item, and whether it is tagged for job posting.
 *
 * The rows are DERIVED from the plantilla — an item is listed while it and its post are live and
 * nobody holds it — so there is no status axis and nothing here to delete. Create TAGS: the one
 * thing this module writes is the decision to advertise a vacancy. The folder tabs split the
 * list on that decision, and the page LANDS on For job posting (product call, 15 Oct 2026): the
 * resting question is what is being advertised.
 *
 * **Published** (22 Oct 2026) is the step after tagging — a tagged posting takes no applicant and is
 * not in Deliberate until it is published. Three folders now, in the cycle's order: For job posting
 * (tagged, not yet published) · Published · Not tagged; the status pill reads the same three.
 */
export function VacanciesIndexPage() {
  const navigate = useNavigate();
  const toast = useToast();

  /** Null = closed; otherwise open, optionally seeded with the row it was opened from. */
  const [tagging, setTagging] = useState<{ itemId?: string } | null>(null);
  /** The selection bar's tag — the selected rows, through a form of their own. */
  const [taggingSelected, setTaggingSelected] = useState<VacancyListItem[] | null>(null);
  const [pending, setPending] = useState<Pending | null>(null);
  const [running, setRunning] = useState(false);
  const [error, setError] = useState<ApiError | null>(null);
  const [reloadKey, setReloadKey] = useState(0);

  const refresh = useCallback(() => setReloadKey((k) => k + 1), []);

  const fetchVacancies = useCallback(
    (query: IndexQueryState, signal: AbortSignal) =>
      vacanciesApi.list(
        {
          search: query.search,
          page: query.page,
          pageSize: query.pageSize,
          sort: query.sort ?? undefined,
          unit: query.facets.unit?.join(','),
          jobPosting: query.facets.jobPosting?.join(','),
        },
        signal,
      ),
    [],
  );

  const rowActions = useCallback(
    (vacancy: VacancyListItem): MenuItem[] => [
      {
        key: `tag-${vacancy.id}`,
        label: 'Tag for job posting',
        icon: <Megaphone className="h-4 w-4" aria-hidden="true" />,
        permission: 'vacancies.create',
        disabledReason: vacancy.isForJobPosting ? 'This item is already tagged for job posting.' : null,
        // Opens the form on this item's position, the item already ticked.
        onClick: () => setTagging({ itemId: vacancy.id }),
      },
      {
        // The step after tagging — opens the posting to applicants and to Deliberate.
        key: `publish-${vacancy.id}`,
        label: 'Publish',
        icon: <Send className="h-4 w-4" aria-hidden="true" />,
        permission: 'vacancies.publish',
        disabledReason: !vacancy.isForJobPosting
          ? 'Tag this item for job posting before publishing it.'
          : vacancy.isPublished
            ? 'This job posting is already published.'
            : null,
        onClick: () => setPending({ kind: 'publish', vacancies: [vacancy] }),
      },
      {
        key: `untag-${vacancy.id}`,
        label: 'Remove tag',
        icon: <MegaphoneOff className="h-4 w-4" aria-hidden="true" />,
        permission: 'vacancies.delete',
        disabledReason: vacancy.isForJobPosting ? null : 'This item is not tagged for job posting.',
        onClick: () => setPending({ kind: 'untag', vacancy }),
      },
    ],
    [],
  );

  /*
    A selection can span several positions, and the tag form holds ONE — so the bulk entry skips the
    picking and opens <TagSelectedModal>, which asks only the posting details, once for all of them.
  */
  const bulkActions = useCallback((selected: VacancyListItem[]): MenuItem[] => {
    const untagged = selected.filter((v) => !v.isForJobPosting);
    const publishable = selected.filter((v) => v.isForJobPosting && !v.isPublished);

    return [
      {
        key: 'bulk-tag',
        label: 'Tag for job posting',
        icon: <Megaphone className="h-4 w-4" aria-hidden="true" />,
        permission: 'vacancies.create',
        disabledReason: untagged.length === 0 ? 'Every selected item is already tagged.' : null,
        onClick: () => setTaggingSelected(untagged),
      },
      {
        key: 'bulk-publish',
        label: `Publish ${publishable.length} posting${publishable.length === 1 ? '' : 's'}`,
        icon: <Send className="h-4 w-4" aria-hidden="true" />,
        permission: 'vacancies.publish',
        disabledReason:
          publishable.length === 0 ? 'No selected item is tagged and waiting to be published.' : null,
        onClick: () => setPending({ kind: 'publish', vacancies: publishable }),
      },
    ];
  }, []);

  async function runPending() {
    if (!pending) return;

    setRunning(true);
    setError(null);

    let published = 0;

    try {
      if (pending.kind === 'untag') {
        await vacanciesApi.untag(pending.vacancy.id);
        toast.success(`Removed the job posting tag from ${pending.vacancy.itemNumber}.`);
      } else {
        // One request per item, so each lands in its own item's History Logs.
        for (const vacancy of pending.vacancies) {
          await vacanciesApi.publish(vacancy.id);
          published += 1;
        }
        toast.success(`Published ${published} job posting${published === 1 ? '' : 's'}.`);
      }

      setPending(null);
      refresh();
    } catch (caught) {
      setError(caught instanceof ApiError ? caught : new ApiError('The action could not be completed.', 0));
      // A run stopped partway still published some; show what is now true.
      if (published > 0) refresh();
    } finally {
      setRunning(false);
    }
  }

  return (
    <>
      <IndexPage<VacancyListItem>
        module="vacancies"
        breadcrumb={['Human Resources', 'Recruitment', 'Vacancies']}
        columns={COLUMNS}
        facets={[
          { key: 'jobPosting', label: 'Job posting' },
          { key: 'unit', label: 'Office assignment' },
        ]}
        folderNavFacet="jobPosting"
        initialFacets={{ jobPosting: ['tagged'] }}
        /*
          ONE HEADER ROW PER CATALOG POSITION, regardless of unit (product call, 16 Oct 2026). The
          server orders by it and no column sorts, so a group never splits. Keyed on the id, never
          the title; a post with no catalog row groups under itself. Each row keeps its unit in
          Office assignment — one position can be posted in several.
        */
        groupRow={{
          key: (vacancy) => vacancy.positionId ?? `post:${vacancy.plantillaPositionId}`,
          render: (vacancy) => (
            <div className="sticky left-3 flex min-w-0 items-center gap-2 text-xs text-slate-500">
              <span className="truncate">{vacancy.catalogTitle}</span>
              {vacancy.positionCode ? <span className="font-mono">· {vacancy.positionCode}</span> : null}
            </div>
          ),
        }}
        auditEntityTypes={VACANCY_AUDIT_ENTITY_TYPES}
        rowKey={(vacancy) => vacancy.id}
        fetch={fetchVacancies}
        reloadKey={reloadKey}
        searchPlaceholder="Search positions, item numbers or offices"
        emptyMessage="No vacant plantilla item here."
        onCreate={() => setTagging({})}
        createLabel="Tag for job posting"
        createPermission="vacancies.create"
        onRowOpen={(vacancy) => navigate(`/vacancies/${vacancy.id}`)}
        openPermission="vacancies.read"
        rowActions={rowActions}
        bulkActions={bulkActions}
        // The ITEM's provenance — when the slot was authorized. The tag's own who and when are
        // on the profile's banner.
        otherInfo={(vacancy) => vacancy}
      />

      {tagging !== null ? (
        <TagForJobPostingModal
          open
          initialItemId={tagging.itemId}
          onClose={() => setTagging(null)}
          onSaved={() => {
            setTagging(null);
            refresh();
          }}
        />
      ) : null}

      {taggingSelected !== null ? (
        <TagSelectedModal
          vacancies={taggingSelected}
          onClose={() => {
            setTaggingSelected(null);
            // A run stopped partway still tagged rows; show what is now true.
            refresh();
          }}
          onSaved={() => {
            setTaggingSelected(null);
            refresh();
          }}
        />
      ) : null}

      {pending ? (
        <ConfirmDialog
          open
          onClose={() => {
            setPending(null);
            setError(null);
          }}
          onConfirm={() => void runPending()}
          title={
            pending.kind === 'untag'
              ? 'Remove job posting tag'
              : pending.vacancies.length === 1
                ? 'Publish job posting'
                : `Publish ${pending.vacancies.length} job postings`
          }
          /*
            Confirmed, not credentialed: ordinary acts of the job. A removed tag stays as an inactive row
            with its applicants kept; a publication cannot be undone, and the dialog says so.
          */
          requireConfirm
          loading={running}
          id={`vacancy-${pending.kind}-dialog`}
          body={
            <div className="flex flex-col gap-2">
              <p>
                {pending.kind === 'untag' ? (
                  <>
                    <span className="font-mono">{pending.vacancy.itemNumber}</span> stays vacant and moves to Not
                    tagged. The posting ends and takes no new applicants; those recorded are kept. It can be tagged
                    again at any time.
                  </>
                ) : (
                  <>
                    {pending.vacancies.length === 1 ? (
                      <span className="font-mono">{pending.vacancies[0].itemNumber}</span>
                    ) : (
                      `${pending.vacancies.length} postings`
                    )}{' '}
                    {pending.vacancies.length === 1 ? 'moves' : 'move'} to Published: from now on{' '}
                    {pending.vacancies.length === 1 ? 'it takes' : 'they take'} applicants and{' '}
                    {pending.vacancies.length === 1 ? 'appears' : 'appear'} in Deliberate. Publishing cannot be undone;
                    removing the tag still ends a posting.
                  </>
                )}
              </p>

              {error ? (
                <p role="alert" className="rounded-lg border border-red-200 bg-red-50 p-2 text-xs text-red-700">
                  {error.message}
                </p>
              ) : null}
            </div>
          }
        />
      ) : null}
    </>
  );
}

/**
 * The anchor: the ITEM NUMBER — the one thing that differs between the rows of a group.
 *
 * The position is said ONCE, by the table's group header (product call, 16 Oct 2026: "1 catalog
 * of position should only display"), so the table carries no Position title column. The LIST has
 * no group header, so there the position leads and the item number is joined to it (15 Oct 2026)
 * — the Goods Receipts anchor is the precedent for a surface-aware anchor.
 */
function VacancyAnchor({ vacancy }: { vacancy: VacancyListItem }) {
  const onList = useListSurface();

  return onList ? (
    <TextCell value={`${vacancy.positionTitle} — ${vacancy.itemNumber}`} />
  ) : (
    <TextCell value={vacancy.itemNumber} mono />
  );
}

/**
 * The posting's columns, in the product owner's order (15 Oct 2026), then the state — less the
 * Position title, which the group header now carries once per position (16 Oct 2026).
 */
const COLUMNS: ColumnDef<VacancyListItem>[] = [
  {
    key: 'itemNumber',
    header: 'Item no.',
    width: 190,
    render: (vacancy) => <VacancyAnchor vacancy={vacancy} />,
  },
  {
    key: 'salaryGrade',
    header: 'Salary grade',
    width: 130,
    summary: true,
    render: (vacancy) => <TextCell value={vacancy.salaryGradeLabel} />,
  },
  {
    key: 'monthlyAmount',
    header: 'Monthly salary',
    width: 140,
    align: 'right',
    summary: true,
    render: (vacancy) => <TextCell value={formatMoney(vacancy.monthlyAmount)} mono />,
  },
  {
    key: 'education',
    header: 'Education',
    width: 240,
    render: (vacancy) => <TextCell value={vacancy.education} />,
  },
  {
    key: 'training',
    header: 'Training',
    width: 240,
    render: (vacancy) => <TextCell value={vacancy.training} />,
  },
  {
    key: 'experience',
    header: 'Experience',
    width: 240,
    render: (vacancy) => <TextCell value={vacancy.experience} />,
  },
  {
    key: 'eligibilityName',
    header: 'Eligibility',
    width: 200,
    render: (vacancy) => <TextCell value={vacancy.eligibilityName} />,
  },
  {
    key: 'otherExpectations',
    header: 'Other expectation',
    width: 240,
    render: (vacancy) => <TextCell value={vacancy.otherExpectations} />,
  },
  {
    key: 'orgUnitName',
    header: 'Office assignment',
    width: 200,
    summary: true,
    render: (vacancy) => <TextCell value={vacancy.orgUnitName} />,
  },
  {
    /*
      The live posting's LIFECYCLE beside the tag (21 Oct 2026) — Completed while an applicant on it is
      endorsed for appointment (Deliberate). A stage, not the status: a completed posting is still tagged,
      so the tag facet and its pill keep their one classification.
    */
    key: 'postingStage',
    header: 'Posting',
    width: 120,
    stage: true,
    render: (vacancy) =>
      vacancy.isCompleted ? <StatusText tone="inactive">Completed</StatusText> : <TextCell value={null} />,
  },
  {
    // The tag is the row's state — the facet and this pill read ONE classification, three buckets.
    key: 'jobPosting',
    header: 'Job posting',
    width: 140,
    status: true,
    render: (vacancy) =>
      vacancy.isPublished ? (
        <StatusText tone="active">Published</StatusText>
      ) : vacancy.isForJobPosting ? (
        <StatusText tone="active">For job posting</StatusText>
      ) : (
        <StatusText tone="inactive">Not tagged</StatusText>
      ),
  },
];
